import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button } from '@mui/joy';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

const TextSelectionPopover = ({ children, source, onQuote }) => {
  const containerRef = useRef(null);
  const [popover, setPopover] = useState(null);

  const handleMouseUp = useCallback(() => {
    // Small delay to let selection finalize
    setTimeout(() => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();

      if (!text || text.length === 0) {
        setPopover(null);
        return;
      }

      // Check if selection is within our container
      if (!containerRef.current) {
        setPopover(null);
        return;
      }

      const range = selection.getRangeAt(0);
      const container = containerRef.current;

      // Verify selection is inside this container
      if (!container.contains(range.commonAncestorContainer)) {
        setPopover(null);
        return;
      }

      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      setPopover({
        text,
        // Position relative to container
        top: rect.top - containerRect.top - 36,
        left: rect.left - containerRect.left + rect.width / 2,
      });
    }, 10);
  }, []);

  const handleMouseDown = useCallback(() => {
    // Clear popover when starting new selection
    setPopover(null);
  }, []);

  const handleQuoteClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (popover?.text && onQuote) {
        onQuote({ text: popover.text, source });
      }

      // Clear selection and popover
      window.getSelection()?.removeAllRanges();
      setPopover(null);
    },
    [popover, source, onQuote]
  );

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPopover(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close popover on scroll
  useEffect(() => {
    const handleScroll = () => setPopover(null);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  return (
    <Box
      ref={containerRef}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
      sx={{ position: 'relative' }}
    >
      {children}

      {popover && (
        <Box
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: popover.top,
            left: popover.left,
            transform: 'translateX(-50%)',
            zIndex: 1000,
            animation: 'fadeIn 0.15s ease-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateX(-50%) translateY(4px)' },
              to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
            },
          }}
        >
          <Button
            size="sm"
            variant="solid"
            color="neutral"
            startDecorator={<FormatQuoteIcon sx={{ fontSize: 16 }} />}
            onClick={handleQuoteClick}
            sx={{
              boxShadow: 'md',
              fontSize: '12px',
              py: 0.5,
              px: 1.5,
              borderRadius: 'md',
              whiteSpace: 'nowrap',
            }}
          >
            Quote
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TextSelectionPopover;
